import { Box, Tab, Tabs, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Tests from './Tests/Tests';
import CourseOverview from '../Pages/CourseOverview';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useTypedSelector } from '@Features/store';
import usePlatformMode from '@Hooks/usePlatformMode';
import AttendanceTable from './Attendance/AttendanceTable';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ pl: 0, pt: 1.531 }}>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
}

const ReportsTabs = () => {
	const { path } = useRouteMatch();
	const history = useHistory();
	const location = useLocation();
	const { currentClassroom } = useTypedSelector((state) => state.classroom);
	const { whatsappEnabled } = usePlatformMode();

	// Built as ONE array so a conditional tab keeps the Tab index and its
	// TabPanel index in lockstep — a `{flag && <TabPanel/>}` between fixed
	// indices leaves a blank panel.
	const tabs = useMemo(() => {
		const entries: Array<{ label: string; route: string; node: React.ReactNode }> = [
			{ label: 'Overview', route: 'CourseOverview', node: <CourseOverview /> },
			{ label: 'Tests', route: 'test', node: <Tests /> },
		];
		if (currentClassroom?.type === 'center' && whatsappEnabled) {
			entries.push({
				label: 'Attendance',
				route: 'attendance',
				node: <AttendanceTable />,
			});
		}
		return entries;
	}, [currentClassroom?.type, whatsappEnabled]);

	// A refresh on /reports/attendance lands back on that tab.
	const initialTab = tabs.findIndex((tab) =>
		location.pathname.endsWith(`/${tab.route}`)
	);
	const [value, setValue] = useState<number>(initialTab === -1 ? 0 : initialTab);

	// The Attendance tab only exists once the classroom and platform
	// settings load (async) - re-sync a deep link when the tab it points
	// at appears; until then the state above already fell back to 0.
	useEffect(() => {
		if (initialTab !== -1) setValue(initialTab);
	}, [initialTab]);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const backButton = (val: string) => {
		history.replace({ pathname: `${path}/${val}` });
	};

	return (
		<TabWrraper>
			<Container>
				<Tabs value={Math.min(value, tabs.length - 1)} onChange={handleChange}>
					{tabs.map((tab) => (
						<TabStyle
							key={tab.route}
							label={tab.label}
							onClick={() => {
								backButton(tab.route);
							}}
						/>
					))}
				</Tabs>
			</Container>
			{tabs.map((tab, index) => (
				<TabPanel key={tab.route} value={Math.min(value, tabs.length - 1)} index={index}>
					{tab.node}
				</TabPanel>
			))}
		</TabWrraper>
	);
};

const TabWrraper = styled(Box)`
	width: 100%;
	.MuiTabs-indicator {
		border: solid 1px #5ac0fc;
		height: 1px;
	}
	.MuiTab-root {
		font-size: 1rem;
		font-weight: 600;
		color: #afafaf;
		padding-bottom: 0;
		padding-left: 0;
		padding-right: 0;
		min-width: unset;
		:nth-child(even) {
			margin: 0 5.75rem;
		}
	}
	.Mui-selected {
		color: ${(props) => props.theme.palette.primary.main};
	}
`;
const Container = styled(Box)`
	border-bottom: solid 1px #d3d3d3;
`;
const TabStyle = styled(Tab)`
	text-transform: capitalize;
`;

export default ReportsTabs;
